import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Loader2, Sparkles, FlaskConical, AlertTriangle, Milestone, Filter } from "lucide-react";
import { format, parseISO, isBefore, isToday } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
  requirement: { label: "Requirement", color: "bg-purple-500", badge: "bg-purple-50 text-purple-700 border-purple-200", icon: Sparkles },
  uat: { label: "UAT", color: "bg-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-200", icon: FlaskConical },
  painpoint: { label: "Pain Point", color: "bg-red-500", badge: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  milestone: { label: "Milestone", color: "bg-teal-500", badge: "bg-teal-50 text-teal-700 border-teal-200", icon: Milestone },
};

const priorityColors = {
  P0: "text-red-600",
  P1: "text-amber-600",
  P2: "text-blue-600",
  P3: "text-slate-500",
};

export const CalendarPanel = () => {
  const { isAdmin, user, selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("personal");
  const [filterType, setFilterType] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [products, setProducts] = useState([]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === "master" && isAdmin ? "/master-calendar" : "/my-calendar";
      const params = {};
      if (viewMode === "personal" && productId) {
        params.product_id = productId;
      } else if (viewMode === "master" && filterProduct !== "all") {
        params.product_id = filterProduct;
      }
      const res = await axios.get(`${API}${endpoint}`, { params });
      setTasks(res.data);
    } catch (err) {
      console.error("Failed to fetch calendar tasks:", err);
    }
    setLoading(false);
  }, [viewMode, isAdmin, filterProduct, productId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/admin/products`).then(res => setProducts(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  const filteredTasks = tasks.filter(t => {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (filterProduct !== "all" && t.product_id !== filterProduct) return false;
    return true;
  });

  const upcomingTasks = filteredTasks
    .filter(t => {
      if (!t.due_date) return false;
      try {
        const d = parseISO(t.due_date);
        return !isBefore(d, new Date()) || isToday(d);
      } catch { return false; }
    })
    .sort((a, b) => {
      try { return parseISO(a.due_date) - parseISO(b.due_date); }
      catch { return 0; }
    })
    .slice(0, 15);

  const overdueTasks = filteredTasks.filter(t => {
    if (!t.due_date) return false;
    try {
      const d = parseISO(t.due_date);
      return isBefore(d, new Date()) && !isToday(d) && t.status !== "Completed" && t.status !== "Passed" && t.status !== "Solution Completed";
    } catch { return false; }
  });

  return (
    <div data-testid="calendar-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#A78BFA] mb-3">
        Tasks
      </p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">
        {viewMode === "master" ? "All Tasks" : "My Tasks"}
      </h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">
        {viewMode === "master" ? "All tasks and deadlines across products" : "Your assigned tasks and upcoming deadlines"}
      </p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Controls */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="calendar-controls">
        {isAdmin && (
          <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <button
              onClick={() => setViewMode("personal")}
              className={`px-4 py-2 text-xs font-semibold transition-colors ${
                viewMode === "personal" ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              }`}
              data-testid="calendar-personal-btn"
            >
              Personal
            </button>
            <button
              onClick={() => setViewMode("master")}
              className={`px-4 py-2 text-xs font-semibold transition-colors ${
                viewMode === "master" ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              }`}
              data-testid="calendar-master-btn"
            >
              Master
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer"
            data-testid="calendar-filter-type"
          >
            <option value="all">All Types</option>
            <option value="requirement">Requirements</option>
            <option value="uat">UAT</option>
            <option value="painpoint">Pain Points</option>
            <option value="milestone">Milestones</option>
          </select>
        </div>

        {viewMode === "master" && isAdmin && products.length > 0 && (
          <select
            value={filterProduct}
            onChange={(e) => setFilterProduct(e.target.value)}
            className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer"
            data-testid="calendar-filter-product"
          >
            <option value="all">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Summary Badges */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="calendar-summary">
        <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
          {filteredTasks.length} Total Tasks
        </Badge>
        {overdueTasks.length > 0 && (
          <Badge className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
            {overdueTasks.length} Overdue
          </Badge>
        )}
        <Badge className="bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800">
          {upcomingTasks.length} Upcoming
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overdue */}
          {overdueTasks.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Overdue Tasks
              </h3>
              <div className="grid gap-3 md:grid-cols-2">
                {overdueTasks.map(task => (
                  <TaskCard key={task.id} task={task} isOverdue />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-teal-600 dark:text-teal-400" /> Upcoming Deadlines
            </h3>
            {upcomingTasks.length === 0 ? (
              <Card className="card-modern">
                <CardContent className="p-8 text-center">
                  <CalendarDays className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-400 dark:text-slate-500">No upcoming deadlines</p>
                  <p className="text-xs text-slate-300 dark:text-slate-600 mt-1">Set due dates on Requirements or UAT items to see them here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {upcomingTasks.map(task => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TaskCard = ({ task, isOverdue }) => {
  const cfg = typeConfig[task.type] || typeConfig.requirement;
  const Icon = cfg.icon;
  let dateStr = "";
  try {
    dateStr = format(parseISO(task.due_date), "MMM d, yyyy");
  } catch {
    dateStr = task.due_date;
  }

  const isCompletedStatus = task.status === "Completed" || task.status === "Passed" || task.status === "Solution Completed";

  return (
    <Card
      className={`card-modern ${
        isOverdue ? "border-red-200 dark:border-red-800" : ""
      } ${isCompletedStatus ? "opacity-60" : ""}`}
      data-testid={`calendar-task-${task.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isOverdue ? "bg-red-50 dark:bg-red-900/30" : "bg-slate-50 dark:bg-slate-800"
          }`}>
            <Icon className={`w-4 h-4 ${isOverdue ? "text-red-500 dark:text-red-400" : "text-slate-500 dark:text-slate-400"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`text-sm font-semibold ${isCompletedStatus ? "text-slate-400 dark:text-slate-500 line-through" : "text-slate-800 dark:text-slate-100"}`}>
                {task.title}
              </h4>
              {task.priority && (
                <span className={`text-[10px] font-bold ${priorityColors[task.priority] || "text-slate-500"}`}>
                  {task.priority}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={`${cfg.badge} dark:bg-opacity-20 border text-[10px] py-0`}>
                {cfg.label}
              </Badge>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">{task.product_name}</span>
              <span className={`text-[11px] font-medium ${isOverdue ? "text-red-600 dark:text-red-400" : "text-slate-500 dark:text-slate-400"}`}>
                Due: {dateStr}
              </span>
            </div>
            {task.assigned_to && task.assigned_to.length > 0 && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {task.assigned_to.map((name, i) => (
                  <span key={i} className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded-full">
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Badge className={`text-[10px] border flex-shrink-0 ${
            isCompletedStatus ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800" : 
            isOverdue ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800" :
            "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
          }`}>
            {task.status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};
